import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import SourcedLogo from '../brand/SourcedLogo';
import './PrivacyPolicy.css';

function PrivacyPolicy() {
  useEffect(() => {
    document.body.style.backgroundColor = '#ebe4d7';
    document.body.style.color = '#151515';
    return () => {
      document.body.style.backgroundColor = '';
      document.body.style.color = '';
    };
  }, []);

  return (
    <div className="privacy-page">
      {/* Navigation */}
      <nav className="pp-nav">
        <div className="pp-nav-inner">
          <Link to="/" className="pp-nav-logo" aria-label="Sourced home"><SourcedLogo height={20} /></Link>
          <Link to="/" className="pp-nav-back">Back to Home</Link>
        </div>
      </nav>

      <main className="privacy">
        <header className="privacy-hero">
          <span className="pp-eyebrow">Legal</span>
          <h1 className="privacy-title">Privacy Policy</h1>
          <p className="privacy-meta">
            <span>Last Updated: October 12, 2026</span>
            <span className="meta-sep">&bull;</span>
            <span>Effective Date: October 12, 2026</span>
          </p>
        </header>

        <section id="intro" className="policy-card">
          <p>
            This Privacy Policy explains how Sourced (&ldquo;Sourced,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;) collects, uses,
            shares and protects information when you use the Sourced iOS app, the website at sourcedfinds.com, and
            related services (the &ldquo;Service&rdquo;). Sourced finds secondhand and resale clothing listings that match
            outfits you love: you connect Pinterest boards, upload photos, or pick a style, and we search marketplaces
            such as Poshmark, Grailed, Depop and Vinted for pieces in your sizes.
          </p>
        </section>

        <section id="info-we-collect" className="policy-card">
          <h2>1. Information We Collect</h2>

          <h3>a. Information You Provide</h3>
          <ul>
            <li>
              <strong>Account Information:</strong> When you sign in with Apple or Google, we receive a verified account
              identifier from that provider and, if you allow it, your email address and name. With Sign in with Apple
              you may choose to hide your email, in which case we receive a relay address. We never receive your Apple
              or Google password.
            </li>
            <li>
              <strong>Profile Information:</strong> Your first name, username, and an optional profile photo you take
              with your camera or choose from your photo library.
            </li>
            <li>
              <strong>Style and Sizing Preferences:</strong> Your sizing category (e.g. men&rsquo;s or women&rsquo;s), your sizes
              (tops, bottoms, footwear, dresses, tailoring, accessories), favorite brands, and the styles you select.
            </li>
            <li>
              <strong>Outfit Images:</strong> Photos you upload to start a find, and images from the Pinterest boards
              you choose to sync.
            </li>
            <li>
              <strong>Saved Listings:</strong> Listings you save, including their title, brand, price, size and image.
            </li>
            <li>
              <strong>Feedback:</strong> When you give a thumbs up or down on results, we store your rating, any note
              you write, and a snapshot of what you were shown (the reference image, the items we identified, the
              listings, and your sizes) so we can reproduce and fix the issue.
            </li>
            <li>
              <strong>Communications:</strong> Messages you send us, for example by email.
            </li>
          </ul>

          <h3>b. Information from Pinterest</h3>
          <p>
            If you connect Pinterest, you authorize us through Pinterest&rsquo;s OAuth flow. We receive access and refresh
            tokens and use them to read your boards (names, pin counts, and pin images) and the pins in the boards you
            select. We only read data; we never post, edit, or delete anything on your Pinterest account. You can revoke
            Sourced&rsquo;s access at any time in your Pinterest account settings, and deleting your Sourced account
            removes the Pinterest tokens we store.
          </p>

          <h3>c. Information Collected Automatically</h3>
          <ul>
            <li>
              <strong>App Usage and Diagnostics:</strong> The app records events such as screens viewed, buttons and
              listings tapped, finds started, errors and crashes, and the timing and success of requests to our servers.
              Each event carries a random per-install identifier, your Sourced user ID once you are signed in, and the
              app version.
            </li>
            <li>
              <strong>Device Information:</strong> Device model, operating system version, language/locale and time
              zone. Our servers and hosting provider may also log your IP address as part of normal network operation.
            </li>
            <li>
              <strong>Push Notification Token:</strong> If you allow notifications, we store your device&rsquo;s push token so
              we can tell you when your listings are ready.
            </li>
          </ul>
          <p>
            We do not use advertising identifiers, we do not track you across other companies&rsquo; apps or websites, and
            we do not collect your precise location or your contacts.
          </p>

          <h3>d. Our Website</h3>
          <ul>
            <li>
              <strong>Analytics:</strong> sourcedfinds.com uses Google Analytics, which sets cookies and collects
              information such as pages visited, referring site, browser and device type, and approximate location
              derived from your IP address. You can opt out with Google&rsquo;s{' '}
              <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">browser add-on</a>{' '}
              or by blocking cookies.
            </li>
            <li>
              <strong>Beta Signup:</strong> If you join the waitlist, we collect the email address you enter so we can
              contact you about access to Sourced.
            </li>
            <li>
              <strong>Web App:</strong> If you use Sourced in a browser, your sign-in session is kept in your
              browser&rsquo;s local storage and cookies.
            </li>
          </ul>

          <h3>e. Marketplace Listing Data</h3>
          <p>
            To build your results we collect publicly available listing information (titles, prices, sizes, brands,
            images and links) from marketplaces including Poshmark, Grailed, Depop and Vinted. We do not ask for,
            collect, or store your credentials for any marketplace. When you open a listing, you go to that
            marketplace, and any purchase happens there under its terms.
          </p>
        </section>

        <section id="how-we-use" className="policy-card">
          <h2>2. How We Use Your Information</h2>
          <ul className="dash-list">
            <li>To create and secure your account and keep you signed in.</li>
            <li>To analyze your outfit images, identify the items in them, and search marketplaces for matching listings in your sizes and preferred brands.</li>
            <li>To show, save and organize your finds and saved listings, and notify you when results are ready.</li>
            <li>To apply usage limits for your plan (for example, how many custom finds you can start in a given period).</li>
            <li>To fix bugs, understand which features are used, and improve the quality of our matching, including by reviewing feedback you send.</li>
            <li>To respond to you and send important service notices.</li>
            <li>To prevent abuse, enforce our terms, and comply with the law.</li>
          </ul>
          <p>We do not sell your personal information, and we do not use it for targeted advertising.</p>
        </section>

        <section id="image-analysis" className="policy-card">
          <h2>3. Automated Image Analysis</h2>
          <p>
            Images you upload or sync are sent to Google&rsquo;s Gemini models (via Google Cloud Vertex AI) to describe the
            clothing in them and to help choose the best matching listings. Google processes this data on our behalf as
            a service provider under its cloud terms. We do not use your images to identify people, and we do not use
            them for any purpose other than providing and improving the Service.
          </p>
        </section>

        <section id="sharing" className="policy-card">
          <h2>4. How We Share Information</h2>
          <p>We share information only as follows:</p>
          <ul>
            <li><strong>Amazon Web Services:</strong> Hosting, databases, file storage, and push notification delivery (via Apple Push Notification service).</li>
            <li><strong>Google Cloud (Vertex AI / Gemini):</strong> Analysis of outfit images, selection of matching listings, and summarizing feedback for our internal product reports.</li>
            <li><strong>Google Analytics:</strong> Website usage analytics for sourcedfinds.com.</li>
            <li><strong>Netlify:</strong> Hosting our website and receiving beta signup submissions.</li>
            <li><strong>Apple and Google:</strong> Verifying your sign-in.</li>
            <li><strong>Pinterest:</strong> Reading the boards you authorize, using the tokens you grant.</li>
            <li>
              <strong>Proxy and Network Providers:</strong> Routing our searches of public marketplace listings. Search
              terms (e.g. &ldquo;black wool overcoat&rdquo;) are sent; your name, email and account details are not.
            </li>
            <li><strong>Legal and Safety:</strong> When required by law, or to protect the rights, property or safety of Sourced, our users or others.</li>
            <li><strong>Business Transfers:</strong> If Sourced is involved in a merger, acquisition or sale of assets, subject to this policy.</li>
          </ul>
          <p>We may share aggregated or de-identified information that cannot reasonably be used to identify you.</p>
        </section>

        <section id="retention" className="policy-card">
          <h2>5. Data Retention and Account Deletion</h2>
          <p>
            We keep your information for as long as your account is active or as needed to provide the Service. You can
            permanently delete your account at any time in the app (Profile &rarr; Delete Account). Deleting your account
            removes your profile, sizes and preferences, Pinterest tokens, username, saved listings, uploaded and synced
            images, and your find results. Usage diagnostics and feedback reports may be kept for a limited time for
            debugging and product quality; you can ask us to delete them too by emailing us. We may keep information
            where required by law or to resolve disputes.
          </p>
        </section>

        <section id="security" className="policy-card">
          <h2>6. Data Security</h2>
          <p>
            Data is encrypted in transit (HTTPS). App requests are authenticated with short-lived session tokens, stored
            in your device&rsquo;s Keychain, and access to our systems is restricted. No service can guarantee absolute
            security, but we work to protect your information and will notify you of a breach where required by law.
          </p>
        </section>

        <section id="rights" className="policy-card">
          <h2>7. Your Rights and Choices</h2>
          <ul>
            <li><strong>Access, correct, export or delete</strong> your information. Most of it can be edited in the app; for anything else, email us.</li>
            <li><strong>Revoke Pinterest access</strong> in your Pinterest account settings.</li>
            <li><strong>Turn off notifications</strong> or camera/photo access in your iOS Settings, and block or clear cookies in your browser.</li>
            <li><strong>Withdraw consent</strong> where we rely on it.</li>
          </ul>
          <p>
            Depending on where you live (for example, California, the EU/UK, or other jurisdictions), you may have
            additional rights, including the right to know what we collect, to object to or restrict processing, and to
            complain to your local data protection authority. We will not discriminate against you for exercising these
            rights. We respond to requests within the time required by applicable law.
          </p>
        </section>

        <section id="children" className="policy-card">
          <h2>8. Children&rsquo;s Privacy</h2>
          <p>
            The Service is not directed to children under 13 (or the minimum age in your country), and we do not
            knowingly collect their information. If you believe a child has given us personal information, contact us
            and we will delete it.
          </p>
        </section>

        <section id="intl" className="policy-card">
          <h2>9. International Data Transfers</h2>
          <p>
            We are based in the United States, and our service providers process data in the United States and other
            countries. Where required, we rely on appropriate safeguards for transfers of personal data.
          </p>
        </section>

        <section id="changes" className="policy-card">
          <h2>10. Changes to This Policy</h2>
          <p>
            We may update this policy as the Service changes. We will post the new version here with a new &ldquo;Last
            Updated&rdquo; date and, for material changes, notify you in the app or by email.
          </p>
        </section>

        <section id="contact" className="policy-card">
          <h2>11. Contact Us</h2>
          <p>Questions or requests about your privacy:</p>
          <ul className="contact-list">
            <li><strong>help@sourcedfinds.com</strong></li>
            <li><strong>SourcedFinds.com</strong></li>
          </ul>
        </section>
      </main>

      <footer className="pp-footer">
        <div className="pp-footer-inner">
          <span className="pp-footer-brand"><SourcedLogo height={16} /></span>
          <Link to="/" className="pp-footer-link">Back to Home</Link>
        </div>
      </footer>
    </div>
  );
}

export default PrivacyPolicy;
